import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import {
  Gauge,
  TrendingUp,
  AlertOctagon,
  CheckCircle2,
  Clock,
  Zap,
  Activity,
  Calendar,
  Filter,
  Download,
  Tv,
  ArrowRight,
  ShieldAlert,
  Sparkles,
  BarChart3,
  Award,
  Printer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from '@tanstack/react-router';
import { exportToExcel } from '@/lib/excel.utils';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  Cell,
} from 'recharts';

export type MachineCategory = 'ALL' | 'OFFSET' | 'LAMINATION' | 'DIECUT' | 'GLUING' | 'FINISHING';
export type ShiftType = 'ALL' | 'SHIFT_1' | 'SHIFT_2' | 'SHIFT_3';

export default function OeeAnalyticsPage() {
  const [category, setCategory] = useState<MachineCategory>('ALL');
  const [shift, setShift] = useState<ShiftType>('ALL');
  const [timeRange, setTimeRange] = useState<'TODAY' | 'WEEK' | 'MONTH'>('TODAY');

  // Tính ngày bắt đầu / kết thúc
  const dateParams = useMemo(() => {
    const now = new Date();
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
    let from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).toISOString();

    if (timeRange === 'WEEK') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      from = d.toISOString();
    } else if (timeRange === 'MONTH') {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      from = d.toISOString();
    }
    return { dateFrom: from, dateTo: to };
  }, [timeRange]);

  // Query OEE Metrics
  const { data: oeeData, isLoading: isLoadingOee } = useQuery({
    ...trpc.oee.getMachineOeeMetrics.queryOptions({
      category,
      shift,
      dateFrom: dateParams.dateFrom,
      dateTo: dateParams.dateTo,
    }),
  });

  // Query Pareto Analytics
  const { data: paretoData, isLoading: isLoadingPareto } = useQuery({
    ...trpc.oee.getDefectParetoAnalytics.queryOptions({
      dateFrom: dateParams.dateFrom,
      dateTo: dateParams.dateTo,
    }),
  });

  const overall = oeeData?.overall || {
    availability: 0,
    performance: 0,
    quality: 100,
    oee: 0,
    grade: 'NEEDS_IMPROVEMENT',
    activeMachinesCount: 0,
    totalMachinesCount: 0,
  };

  const machines = oeeData?.machines || [];
  const trendTimeline = oeeData?.trendTimeline || [];
  const paretoChartData = paretoData?.paretoData || [];
  const topParetoData = useMemo(() => paretoChartData.slice(0, 10), [paretoChartData]);
  const navigate = useNavigate();

  const gradeLabel = (g: string) =>
    g === 'WORLD_CLASS' ? 'Xuất Sắc' : g === 'GOOD' ? 'Đạt Chuẩn' : 'Cần Tối Ưu';

  const handleExport = () =>
    exportToExcel(
      machines,
      [
        { key: 'code', label: 'Mã thiết bị', exportFn: (m: any) => m.code || m.machineId },
        { key: 'name', label: 'Tên máy' },
        { key: 'category', label: 'Phân nhóm' },
        { key: 'run', label: 'Giờ chạy', exportFn: (m: any) => Number((m.totalRunMinutes / 60).toFixed(1)) },
        { key: 'setup', label: 'Giờ setup', exportFn: (m: any) => Number((m.totalSetupMinutes / 60).toFixed(1)) },
        { key: 'totalQtyOk', label: 'SL thành phẩm' },
        { key: 'totalQtyNg', label: 'SL phế' },
        { key: 'availability', label: 'A (%)' },
        { key: 'performance', label: 'P (%)' },
        { key: 'quality', label: 'Q (%)' },
        { key: 'oee', label: 'OEE (%)' },
        { key: 'grade', label: 'Đánh giá', exportFn: (m: any) => gradeLabel(m.grade) },
      ],
      'oee_thiet_bi',
      { sheetName: 'OEE' },
    );

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
      
      {/* Tiêu đề chỉ hiện khi in báo cáo (thanh header chung đã có tên trang) */}
      <div className="hidden print:flex items-center gap-2.5">
        <span className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
          <Gauge className="w-5 h-5" />
        </span>
        <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
          Hiệu Suất Thiết Bị (OEE)
        </h1>
      </div>

      {/* ── Hàng thao tác: thời gian đầu trái → bộ lọc → nút bên phải ── */}
      <div className="erp-list-filter-row flex flex-wrap items-center gap-2 print:hidden">
        <div className="date-filter-pos0 flex items-center shrink-0">
          <div className="flex rounded-lg bg-white border border-slate-200 p-0.5">
            <button
              onClick={() => setTimeRange('TODAY')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                timeRange === 'TODAY'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Hôm Nay
            </button>
            <button
              onClick={() => setTimeRange('WEEK')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                timeRange === 'WEEK'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              7 Ngày Qua
            </button>
            <button
              onClick={() => setTimeRange('MONTH')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                timeRange === 'MONTH'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              30 Ngày
            </button>
          </div>
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as MachineCategory)}
          className="text-xs h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none"
        >
          <option value="ALL">Toàn Xưởng</option>
          <option value="OFFSET">In Offset</option>
          <option value="LAMINATION">Cán Màng</option>
          <option value="DIECUT">Bế Kim</option>
          <option value="GLUING">Dán Hộp</option>
          <option value="FINISHING">Hoàn Thiện</option>
        </select>

        <select
          value={shift}
          onChange={(e) => setShift(e.target.value as ShiftType)}
          className="text-xs h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none"
        >
          <option value="ALL">Tất cả ca</option>
          <option value="SHIFT_1">Ca 1</option>
          <option value="SHIFT_2">Ca 2</option>
          <option value="SHIFT_3">Ca 3</option>
        </select>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: "/mes/andon" })}
            className="h-8 border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-xs"
          >
            <Tv className="w-3.5 h-3.5 mr-1.5" />
            Andon TV
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="h-8 text-xs text-slate-600">
            <Printer className="w-3.5 h-3.5 mr-1.5" />
            In báo cáo
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!machines.length} className="h-8 text-xs text-slate-600">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Xuất Excel
          </Button>
        </div>
      </div>

      {/* ── 3. The 4 OEE Cards (Tối giản) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card OEE Toàn Xưởng */}
        <Card className="border-2 border-emerald-500 bg-white shadow-sm relative overflow-hidden">
          <CardHeader className="pb-1 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-bold tracking-wider text-emerald-800 uppercase">
                OEE
              </CardTitle>
              {overall.grade === 'WORLD_CLASS' && (
                <Badge className="bg-emerald-600 text-white font-bold text-[10px]">Xuất Sắc</Badge>
              )}
              {overall.grade === 'GOOD' && (
                <Badge className="bg-amber-500 text-white font-bold text-[10px]">Đạt Chuẩn</Badge>
              )}
              {overall.grade === 'NEEDS_IMPROVEMENT' && (
                <Badge className="bg-rose-500 text-white font-bold text-[10px]">Cần Cải Tiến</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pb-3 px-4">
            <div className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
              {overall.oee}%
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  overall.oee >= 85 ? 'bg-emerald-500' : overall.oee >= 65 ? 'bg-amber-500' : 'bg-rose-500'
                }`}
                style={{ width: `${overall.oee}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Card Availability (A) */}
        <Card className="shadow-sm bg-white">
          <CardHeader className="pb-1 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                Mức Sẵn Sàng (A)
              </CardTitle>
              <Clock className="w-4 h-4 text-indigo-500" />
            </div>
          </CardHeader>
          <CardContent className="pb-3 px-4">
            <div className="text-3xl lg:text-4xl font-black text-indigo-600">
              {overall.availability}%
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2.5 overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${overall.availability}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Card Performance (P) */}
        <Card className="shadow-sm bg-white">
          <CardHeader className="pb-1 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                Hiệu Suất (P)
              </CardTitle>
              <Zap className="w-4 h-4 text-cyan-500" />
            </div>
          </CardHeader>
          <CardContent className="pb-3 px-4">
            <div className="text-3xl lg:text-4xl font-black text-cyan-600">
              {overall.performance}%
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2.5 overflow-hidden">
              <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${overall.performance}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Card Quality (Q) */}
        <Card className="shadow-sm bg-white">
          <CardHeader className="pb-1 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                Chất Lượng (Q)
              </CardTitle>
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            </div>
          </CardHeader>
          <CardContent className="pb-3 px-4">
            <div className="text-3xl lg:text-4xl font-black text-emerald-600">
              {overall.quality}%
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 mt-2.5 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${overall.quality}%` }} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── 4. Deep Visual Analytics: OEE Trend & Pareto Defect ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Biểu đồ xu hướng OEE */}
        <Card className="shadow-sm bg-white">
          <CardHeader className="pb-2 pt-4 px-5">
            <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-900">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              Xu Hướng OEE (7 Ngày)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="h-[280px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={280}>
                <LineChart data={trendTimeline} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                  <Line type="monotone" dataKey="oee" name="OEE %" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="availability" name="Mức Sẵn Sàng (A)" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="performance" name="Hiệu Suất (P)" stroke="#06b6d4" strokeWidth={2} strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="quality" name="Chất Lượng (Q)" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Biểu đồ Pareto phế phẩm */}
        <Card className="shadow-sm bg-white min-w-0">
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-900">
                <AlertOctagon className="w-4 h-4 text-rose-500" />
                Pareto Phế Phẩm (80/20)
              </CardTitle>
              <Badge variant="outline" className="text-xs text-rose-600 border-rose-200">
                Tổng: {(paretoData?.totalDefectQuantity || 0).toLocaleString()} phế
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {topParetoData.length === 0 ? (
              <div className="h-[280px] flex flex-col items-center justify-center text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mb-1.5" />
                <p className="text-sm font-bold text-slate-800">Không có dữ liệu phế phẩm trong kỳ</p>
              </div>
            ) : (
              <div className="h-[280px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={280}>
                  <ComposedChart data={topParetoData} margin={{ top: 10, right: 20, left: -10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="reason" 
                      tick={{ fontSize: 10 }} 
                      interval={0} 
                      angle={-20} 
                      textAnchor="end" 
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                    <RechartsTooltip />
                    <Legend wrapperStyle={{ fontSize: '11px', bottom: -10 }} />
                    <Bar yAxisId="left" dataKey="quantity" name="Số lượng phế" fill="#f43f5e" radius={[4, 4, 0, 0]}>
                      {topParetoData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.isVitalFew ? '#e11d48' : '#94a3b8'} />
                      ))}
                    </Bar>
                    <Line yAxisId="right" type="monotone" dataKey="cumulativePercentage" name="% Tích Lũy (80/20)" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── 5. Machine Benchmark Table (Tối giản) ── */}
      <Card className="shadow-sm bg-white">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-900">
            <BarChart3 className="w-4 h-4 text-emerald-600" />
            Xếp Hạng Thiết Bị
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                  <th className="p-3">Mã Thiết Bị</th>
                  <th className="p-3">Tên Máy</th>
                  <th className="p-3">Phân Nhóm</th>
                  <th className="p-3 text-right">Giờ Chạy (Run)</th>
                  <th className="p-3 text-right">Giờ Setup</th>
                  <th className="p-3 text-right">SL Thành Phẩm</th>
                  <th className="p-3 text-right">SL Phế</th>
                  <th className="p-3 text-center">A (%)</th>
                  <th className="p-3 text-center">P (%)</th>
                  <th className="p-3 text-center">Q (%)</th>
                  <th className="p-3 text-center font-bold">OEE (%)</th>
                  <th className="p-3 text-center">Đánh Giá</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-mono">
                {machines.map((m: any) => {
                  const runHours = (m.totalRunMinutes / 60).toFixed(1);
                  const setupHours = (m.totalSetupMinutes / 60).toFixed(1);

                  return (
                    <tr
                      key={m.id}
                      onClick={() => navigate({ to: "/machines/$machineId", params: { machineId: m.id } })}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer"
                    >
                      <td className="p-3 font-semibold text-indigo-600">{m.code || m.machineId}</td>
                      <td className="p-3 font-sans font-medium text-slate-900">{m.name}</td>
                      <td className="p-3 font-sans text-slate-500">{m.category}</td>
                      <td className="p-3 text-right text-slate-700">{runHours}h</td>
                      <td className="p-3 text-right text-slate-700">{setupHours}h</td>
                      <td className="p-3 text-right text-emerald-600 font-semibold">
                        {m.totalQtyOk.toLocaleString()}
                      </td>
                      <td className="p-3 text-right text-rose-500">
                        {m.totalQtyNg.toLocaleString()}
                      </td>
                      <td className="p-3 text-center">{m.availability}%</td>
                      <td className="p-3 text-center">{m.performance}%</td>
                      <td className="p-3 text-center">{m.quality}%</td>
                      <td className="p-3 text-center text-sm font-bold text-slate-900">
                        {m.oee}%
                      </td>
                      <td className="p-3 text-center font-sans">
                        {m.grade === 'WORLD_CLASS' && (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                            Xuất Sắc
                          </Badge>
                        )}
                        {m.grade === 'GOOD' && (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                            Đạt Chuẩn
                          </Badge>
                        )}
                        {m.grade === 'NEEDS_IMPROVEMENT' && (
                          <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                            Cần Tối Ưu
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
